import React from 'react';
import { Plug, RefreshCw, Unplug } from 'lucide-react';
import { Sheet } from '../ui/Sheet';
import { Field } from '../ui/Field';
import { TransportPicker } from '../TransportPicker';
import { Chip } from '../ui/Chip';
import { LINK_LIMITS, connectionLabel, connectionTone } from '../../../lib/rcu/catalog/link';
import { RCU_HTTP_PORT } from '../../../lib/rcu/catalog/protocol';
import { rcuHost } from '../../../lib/rcu/transport/addressing';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../../stores/rcuLinkStore';
import { useToastStore } from '../../../stores/toastStore';

export interface LinkSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The one link there is: the gateway that holds the broker connection.
 *
 * There used to be a second route, the broker's own WebSocket listener, and two
 * sheets to keep them apart. It is gone — a browser cannot open a raw MQTT
 * socket, and this broker's `wss://` certificate is issued for the literal name
 * `Server`, so no TLS route to it exists either. One route, one sheet, one set of
 * credentials: the gateway's, because the gateway is what holds the broker's.
 */
export const LinkSheet: React.FC<LinkSheetProps> = ({ isOpen, onClose }) => {
  const config = useRcuConfigStore((state) => state.config);
  const settings = useRcuLinkStore((state) => state.settings);
  const link = useRcuLinkStore((state) => state.link);
  const connecting = useRcuLinkStore((state) => state.connecting);
  const demo = useRcuLinkStore((state) => state.demo);
  const updateSettings = useRcuLinkStore((state) => state.updateSettings);
  const connect = useRcuLinkStore((state) => state.connect);
  const disconnect = useRcuLinkStore((state) => state.disconnect);
  const setDemo = useRcuLinkStore((state) => state.setDemo);
  const info = useToastStore((state) => state.info);
  const success = useToastStore((state) => state.success);
  const error = useToastStore((state) => state.error);

  const host = rcuHost(config.rcu);

  const closeSession = (): void => {
    disconnect();
    info('Session closed. It reopens when the broker settings change or the app reloads.');
  };

  const apply = async (): Promise<void> => {
    const opened = await connect(config);
    if (opened) {
      success('Broker session opened. Acks and status will appear here.');
      return;
    }
    /** Read after the attempt: the rendered `link` is the one before it. */
    error(useRcuLinkStore.getState().link.detail);
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title="Device link"
      subtitle="The bridge session, its topics and the demo conversation"
      footer={
        <>
          <button
            type="button"
            onClick={closeSession}
            className="flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#F44336] hover:text-[#C62828]"
          >
            <Unplug className="h-4 w-4" />
            Disconnect
          </button>
          <button
            type="button"
            onClick={() => void apply()}
            disabled={connecting}
            className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
          >
            <Plug className="h-4 w-4" />
            {connecting ? 'Connecting…' : 'Connect'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3 rounded-2xl bg-[#F5F5F5] px-4 py-3">
          <Chip label={connectionLabel(link.status)} tone={connectionTone(link.status)} />
          <p className="min-w-0 flex-1 text-sm text-[#666666]">{link.detail}</p>
        </div>

        <TransportPicker variant="full" />

        <Field
          label="Bridge URL"
          value={settings.bridgeUrl}
          onChange={(bridgeUrl) => updateSettings({ bridgeUrl })}
          placeholder="The address this app was served from"
          hint="Left empty, the bridge is wherever this app came from — the usual arrangement, where one host serves both. Type an address only to reach a gateway somewhere else."
        />
        <Field
          label="Username"
          value={settings.bridgeUsername}
          onChange={(bridgeUsername) => updateSettings({ bridgeUsername })}
          hint="The bridge's own account, not the broker's. Prefilled."
        />
        <Field
          label="Password"
          value={settings.bridgePassword}
          onChange={(bridgePassword) => updateSettings({ bridgePassword })}
          secret
          hint="Stored on this tablet only, and never shown in the event feed."
        />

        <section className="rounded-2xl border border-[#E0E0E0] px-4 py-3">
          <h3 className="text-sm font-medium text-[#333333]">Topics</h3>
          {link.topics.length === 0 ? (
            <p className="mt-1 text-sm text-[#666666]">
              No session. The console subscribes to the serial number’s topics, so the RCU details
              need a serial first.
            </p>
          ) : (
            <ul className="mt-2 space-y-1 font-mono text-xs text-[#666666]">
              {link.topics.map((topic) => (
                <li key={topic} className="break-all">
                  {topic}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-[#E0E0E0] px-4 py-3 text-sm text-[#666666]">
          <h3 className="text-sm font-medium text-[#333333]">What this link can and cannot do</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Commands are published on demand, never retained: a queued command would move a load later.</li>
            <li>
              The device’s own HTTP port ({RCU_HTTP_PORT}) accepts commands but cannot be read from a
              browser: with no session, a command is delivered and unconfirmed.
            </li>
            <li>Bodies stay under {LINK_LIMITS.MAX_COMMAND_BYTES} bytes, which is what the device accepts over HTTP too.</li>
            <li>The RCU also answers on UDP 56000, which no browser can listen to.</li>
            <li>
              The session belongs to the app, not to this console: the room controls read the
              device’s state from it, so locking the console does not close it. Disconnect ends it
              until the settings change or the app reloads.
            </li>
          </ul>
        </section>

        {import.meta.env.DEV && (
          <section className="rounded-2xl border border-dashed border-[#E0E0E0] px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-[#333333]">Demo conversation</h3>
                <p className="mt-0.5 text-xs text-[#666666]">
                  Replays the reference’s recorded replies through the same decoder, for reviewing
                  the console without a device.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDemo(!demo)}
                className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  demo ? 'bg-[#D4A054] text-[#1A1A1A]' : 'border border-[#E0E0E0] text-[#666666]'
                }`}
              >
                <RefreshCw className="h-4 w-4" />
                {demo ? 'On' : 'Off'}
              </button>
            </div>
          </section>
        )}

        {host !== null && (
          <p className="text-xs text-[#666666]">
            Direct HTTP commands go to <span className="font-mono">{host}</span>.
          </p>
        )}
        {settings.bridgeUrl.trim() === '' && (
          <p className="text-xs text-[#666666]">
            No address, so the console looks for the bridge at the address this page was served
            from. Without it commands still go out over HTTP, but no acknowledgement, status or log
            line arrives.
          </p>
        )}
      </div>
    </Sheet>
  );
};

export default LinkSheet;
